// Same limits as the api, which rejects a longer label or a longer list.
export const TASK_LABEL_MAX_LENGTH = 80;
export const TASKS_MAX_COUNT = 50;

export const TASK_SOURCE = {
    QUALIFICATION: "qualification",
    COMPANY: "company",
};

// Duplicate key only, never stored. Kept identical in api
// (missionTemplateHelpers.js) and app.admin (templateFormData.js).
export const normalizeTaskLabel = (label) =>
    String(label ?? "")
        .trim()
        .replace(/\s+/g, " ")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "");

/** The label as the api stores it: trimmed, inner spaces collapsed. */
export const cleanTaskLabel = (label) =>
    typeof label === "string" ? label.trim().replace(/\s+/g, " ") : "";

/**
 * The qualification picked at step 1, as loaded for the dropdown. A name can
 * exist in several sectors: the sector decides when it is known.
 */
export const findQualification = (qualifications, name, type) => {
    if (!Array.isArray(qualifications) || !name) return null;
    const candidates = qualifications.filter((q) => q?.name === name);
    return (
        (type && candidates.find((q) => q.type === type)) ||
        candidates[0] ||
        null
    );
};

/** What a qualification suggests: its non archived tasks, sorted by `order`. */
export const getQualificationTaskProposals = (qualification) => {
    const tasks = Array.isArray(qualification?.tasks)
        ? qualification.tasks
        : [];
    return tasks
        .filter((task) => task && !task.archived && cleanTaskLabel(task.label))
        .slice()
        .sort(
            (a, b) =>
                (a.order ?? Number.MAX_SAFE_INTEGER) -
                (b.order ?? Number.MAX_SAFE_INTEGER)
        )
        .map((task) => ({
            qualificationTaskId: task._id ? String(task._id) : "",
            label: cleanTaskLabel(task.label),
        }));
};

let companyTaskSeq = 0;
const nextCompanyTaskKey = () => {
    companyTaskSeq += 1;
    return `company-${companyTaskSeq}`;
};

const qualificationRowKey = (task) =>
    task.qualificationTaskId
        ? `qualification-${task.qualificationTaskId}`
        : `qualification-label-${normalizeTaskLabel(task.label)}`;

/**
 * Check state of an added task: the AI check runs after the row is shown.
 * Lives on the row only, never in the payload.
 */
export const TASK_CHECK_STATUS = {
    PENDING: "pending",
    VERIFIED: "verified",
};

// Same delay as the facade call, so saving never waits longer than the check.
export const TASK_CHECK_TIMEOUT_MS = 15000;

// Context sent with the check: the api caps it at 120 characters.
const TASK_CHECK_JOB_TITLE_MAX_LENGTH = 120;

const companyRow = (label, checkStatus = TASK_CHECK_STATUS.VERIFIED) => ({
    key: nextCompanyTaskKey(),
    label,
    source: TASK_SOURCE.COMPANY,
    checked: true,
    checkStatus,
});

const qualificationRow = (task, checked) => ({
    key: qualificationRowKey(task),
    label: task.label,
    source: TASK_SOURCE.QUALIFICATION,
    qualificationTaskId: task.qualificationTaskId || "",
    checked,
});

// A label already listed is dropped: the first row wins, as in the api.
const withoutDuplicateLabels = (rows) => {
    const seen = new Set();
    return rows.filter((row) => {
        const key = normalizeTaskLabel(row.label);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

/**
 * Rows of the Tasks step (`templateData.tasks`): the tasks added by the
 * company first, in display order, then the qualification tasks.
 */
export const buildTaskRows = ({ proposals = [], templateTasks = [] } = {}) => {
    const stored = (Array.isArray(templateTasks) ? templateTasks : [])
        .map((task) => ({
            label: cleanTaskLabel(task?.label),
            source: task?.source,
            qualificationTaskId: task?.qualificationTaskId
                ? String(task.qualificationTaskId)
                : "",
        }))
        .filter((task) => task.label);

    // A template without tasks behaves like a new one: everything checked.
    if (stored.length === 0) {
        return withoutDuplicateLabels(
            proposals.map((task) => qualificationRow(task, true))
        );
    }

    const companyRows = stored
        .filter((task) => task.source === TASK_SOURCE.COMPANY)
        .map((task) => companyRow(task.label));
    const storedQualificationTasks = stored.filter(
        (task) => task.source !== TASK_SOURCE.COMPANY
    );
    const storedById = new Map(
        storedQualificationTasks
            .filter((task) => task.qualificationTaskId)
            .map((task) => [task.qualificationTaskId, task])
    );
    const storedLabelKeys = new Set(
        storedQualificationTasks
            .filter((task) => !task.qualificationTaskId)
            .map((task) => normalizeTaskLabel(task.label))
    );
    const matched = new Set();
    const proposalRows = proposals.map((task) => {
        const byId =
            task.qualificationTaskId && storedById.get(task.qualificationTaskId);
        if (byId) {
            matched.add(byId);
            // The template keeps the label it was saved with.
            return qualificationRow({ ...task, label: byId.label }, true);
        }
        const labelKey = normalizeTaskLabel(task.label);
        const byLabel = storedLabelKeys.has(labelKey);
        if (byLabel) {
            matched.add(
                storedQualificationTasks.find(
                    (stored) =>
                        !stored.qualificationTaskId &&
                        normalizeTaskLabel(stored.label) === labelKey
                )
            );
        }
        return qualificationRow(task, byLabel);
    });
    // Saved tasks the qualification no longer offers (archived, removed)
    // stay listed and checked, so saving again does not drop them silently.
    const keptRows = storedQualificationTasks
        .filter((task) => !matched.has(task))
        .map((task) => qualificationRow(task, true));

    return withoutDuplicateLabels([...companyRows, ...proposalRows, ...keptRows]);
};

/** New qualification: its proposals replace the old ones, all checked; added tasks stay. */
export const replaceProposedTasks = (rows, proposals) =>
    withoutDuplicateLabels([
        ...(rows || []).filter((row) => row.source === TASK_SOURCE.COMPANY),
        ...proposals.map((task) => qualificationRow(task, true)),
    ]);

export const ADD_TASK_ERROR = {
    EMPTY: "empty",
    DUPLICATE: "duplicate",
    TOO_MANY: "tooMany",
    REJECTED: "rejected",
};

/**
 * Returns `{ rows, key, label }` with the new row first and pending its AI
 * check, or `{ error }` when the label cannot be added.
 */
export const addCompanyTask = (rows, label) => {
    const cleaned = cleanTaskLabel(label).slice(0, TASK_LABEL_MAX_LENGTH);
    if (!cleaned) return { error: ADD_TASK_ERROR.EMPTY };
    const current = rows || [];
    const key = normalizeTaskLabel(cleaned);
    if (current.some((row) => normalizeTaskLabel(row.label) === key)) {
        return { error: ADD_TASK_ERROR.DUPLICATE };
    }
    if (countSelectedTasks(current) >= TASKS_MAX_COUNT) {
        return { error: ADD_TASK_ERROR.TOO_MANY };
    }
    const row = companyRow(cleaned, TASK_CHECK_STATUS.PENDING);
    return { rows: [row, ...current], key: row.key, label: row.label };
};

export const isTaskCheckPending = (row) =>
    row?.source === TASK_SOURCE.COMPANY &&
    row.checkStatus === TASK_CHECK_STATUS.PENDING;

export const hasPendingTaskChecks = (rows) =>
    (rows || []).some(isTaskCheckPending);

/** Body of the check call: `jobTitle` only when a qualification is picked. */
export const buildTaskCheckBody = (label, jobTitle) => {
    const title = cleanTaskLabel(jobTitle).slice(
        0,
        TASK_CHECK_JOB_TITLE_MAX_LENGTH
    );
    return title ? { label, jobTitle: title } : { label };
};

/**
 * Applies the check answer to the row `key` only. Returns `{ rows }`, plus
 * `{ error, label }` when the row is dropped. Anything but a usable
 * `accepted` or a `rejected` (unavailable, network error, timeout, odd
 * body) keeps the typed label. A row removed meanwhile is not recreated.
 */
export const applyTaskCheckResult = (rows, key, result) => {
    const current = rows || [];
    const row = current.find(
        (candidate) => candidate.key === key && isTaskCheckPending(candidate)
    );
    if (!row) return { rows: current };

    const withoutRow = () =>
        current.filter((candidate) => candidate.key !== key);
    const settle = (label) =>
        current.map((candidate) =>
            candidate.key === key
                ? {
                      ...candidate,
                      label,
                      checkStatus: TASK_CHECK_STATUS.VERIFIED,
                  }
                : candidate
        );

    if (result?.status === "rejected") {
        return {
            rows: withoutRow(),
            error: ADD_TASK_ERROR.REJECTED,
            label: row.label,
        };
    }
    const accepted =
        result?.status === "accepted" ? cleanTaskLabel(result.label) : "";
    if (!accepted || accepted.length > TASK_LABEL_MAX_LENGTH) {
        return { rows: settle(row.label) };
    }
    const acceptedKey = normalizeTaskLabel(accepted);
    const isDuplicate = current.some(
        (candidate) =>
            candidate.key !== key &&
            normalizeTaskLabel(candidate.label) === acceptedKey
    );
    if (isDuplicate) {
        return {
            rows: withoutRow(),
            error: ADD_TASK_ERROR.DUPLICATE,
            label: accepted,
        };
    }
    return { rows: settle(accepted) };
};

export const removeCompanyTask = (rows, key) =>
    (rows || []).filter(
        (row) => !(row.key === key && row.source === TASK_SOURCE.COMPANY)
    );

export const toggleProposedTask = (rows, key) =>
    (rows || []).map((row) =>
        row.key === key && row.source === TASK_SOURCE.QUALIFICATION
            ? { ...row, checked: !row.checked }
            : row
    );

const isSelected = (row) =>
    row.source === TASK_SOURCE.COMPANY || !!row.checked;

export const countSelectedTasks = (rows) =>
    (rows || []).filter(isSelected).length;

/**
 * Payload `tasks`: added tasks in display order, then the checked
 * qualification tasks in the qualification order.
 */
export const buildTasksPayload = (rows) => {
    const selected = (rows || []).filter(isSelected);
    const added = selected
        .filter((row) => row.source === TASK_SOURCE.COMPANY)
        .map((row) => ({ label: row.label, source: TASK_SOURCE.COMPANY }));
    const proposed = selected
        .filter((row) => row.source === TASK_SOURCE.QUALIFICATION)
        .map((row) => ({
            label: row.label,
            source: TASK_SOURCE.QUALIFICATION,
            ...(row.qualificationTaskId
                ? { qualificationTaskId: row.qualificationTaskId }
                : {}),
        }));
    return [...added, ...proposed];
};

/** Labels shown in summaries, as typed (no case change: "CACES" stays). */
export const getTemplateTaskLabels = (tasks) =>
    (Array.isArray(tasks) ? tasks : [])
        .map((task) => cleanTaskLabel(task?.label))
        .filter(Boolean);

// One pass, so a label containing "{second}" or "$&" is shown as typed.
const fillPlaceholders = (template, values) =>
    template.replace(/\{(\w+)\}/g, (match, name) =>
        Object.prototype.hasOwnProperty.call(values, name) ? values[name] : match
    );

/** "A" · "A et B" · "A, B et 1 autre" · "A, B et 4 autres"; "" when empty. */
export const formatTaskSummary = (labels, language) => {
    const list = Array.isArray(labels) ? labels.filter(Boolean) : [];
    if (list.length === 0) return "";
    if (list.length === 1) return list[0];
    const t = (key, fallback) =>
        language.getValue(`missionTemplate.summary.${key}`, fallback);
    if (list.length === 2) {
        return fillPlaceholders(t("tasksTwo", "{first} et {second}"), {
            first: list[0],
            second: list[1],
        });
    }
    const others = list.length - 2;
    const template =
        others === 1
            ? t("tasksManyOne", "{first}, {second} et {count} autre")
            : t("tasksMany", "{first}, {second} et {count} autres");
    return fillPlaceholders(template, {
        first: list[0],
        second: list[1],
        count: String(others),
    });
};
